'use client';

import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import React, { useEffect, useRef, useState } from 'react';
import Try7DaysOk from '@/features/subscription/components/sides/Try7DaysOk';
import Try7Days from '@/features/subscription/components/sides/Try7Days';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import { RootState } from '@/store';
import { hideSide } from '@/store/slices/sideSlice';
import Footer from '@/components/Layouts/Default/Partials/Footer';
import PaymentScreen from '@/features/subscription/components/sides/payment-screen/PaymentScreen';
import ToolLocked from '@/features/subscription/components/sides/ToolLocked';
import PaymentSuccess from '@/features/subscription/components/sides/PaymentSuccess';
import PaymentFailed from '@/features/subscription/components/sides/PaymentFailed';
import { NextUI } from '@/components/NextUI';
import { COMMON_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import useCheckout from '@/features/subscription/hooks/useCheckout';
import {
  setFreeTypeForUserAccessLevel,
  setTypeForUserAccessLevel,
} from '@/store/slices/userAccessSlice';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';

const SideLazyLoading = () => (
  <div className="flex items-center justify-center py-20">
    <NextUI.Spinner size="lg" />
  </div>
);

const Login = dynamic(() => import('@/features/auth/components/Sides/Login'), {
  ssr: false,
  loading: SideLazyLoading,
});
const Signup = dynamic(
  () => import('@/features/auth/components/Sides/Signup'),
  { ssr: false, loading: SideLazyLoading }
);
const ForgotPassword = dynamic(
  () => import('@/features/auth/components/Sides/ForgotPassword'),
  { ssr: false, loading: SideLazyLoading }
);
const ResetPassword = dynamic(
  () => import('@/features/auth/components/Sides/ResetPassword'),
  { ssr: false, loading: SideLazyLoading }
);
const SignupVerification = dynamic(
  () => import('@/features/auth/components/Sides/SignupVerification'),
  { ssr: false, loading: SideLazyLoading }
);
const RecoverEmail = dynamic(
  () => import('@/features/auth/components/Sides/RecoverEmail'),
  { ssr: false, loading: SideLazyLoading }
);
const EmailChange = dynamic(
  () => import('@/features/auth/components/Sides/EmailChange'),
  { ssr: false, loading: SideLazyLoading }
);
const ProForTeams = dynamic(
  () => import('@/features/contact/components/sides/ProForTeams'),
  { ssr: false, loading: SideLazyLoading }
);
const TeamInvite = dynamic(
  () => import('@/features/contact/components/sides/TeamInvite'),
  { ssr: false, loading: SideLazyLoading }
);
const TeamManage = dynamic(
  () => import('@/features/contact/components/sides/TeamManage'),
  { ssr: false, loading: SideLazyLoading }
);

class SideLazyErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('Failed to load side panel screen', error);
  }

  render() {
    if (this.state.hasError) {
      return <SideLazyError />;
    }

    return this.props.children;
  }
}

const SideLazyError = () => {
  const t = useTranslations('sides.lazy_status');

  return (
    <div className="flex items-center justify-center py-20 px-4 text-center">
      <p className="text-gray-dark text-sm">{t('error')}</p>
    </div>
  );
};

const sideMap = {
  login: Login,
  register: Signup,
  forgotPassword: ForgotPassword,
  resetPassword: ResetPassword,
  signupVerification: SignupVerification,
  try7DaysOk: Try7DaysOk,
  try7Days: Try7Days,
  emailChange: EmailChange,
  recoverEmail: RecoverEmail,
  toolLocked: ToolLocked,
  proForTeams: ProForTeams,
  paymentScreen: PaymentScreen,
  paymentSuccess: PaymentSuccess,
  paymentFailed: PaymentFailed,
  teamInvite: TeamInvite,
  teamManage: TeamManage,
};

const initialSideList = ["try7DaysOk"];

const Side: React.FC = () => {
  const dispatch = useAppDispatch();
  const { closeCheckout } = useCheckout();
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );

  const { activeSide, sideData } = useAppSelector(
    (state: RootState) => state.side
  );
  const paywallType = resolveAnalyticsPaywallType({
    paywallType: typeForUserAccessLevel,
    toolType: sideData?.toolType,
  });

  const [SideComponent, setSideComponent] =
    React.useState<React.ComponentType<any> | null>(null);
  const sideComponentTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (activeSide && !initialSideList.includes(activeSide)) {
      const DynamicModal = sideMap[activeSide as keyof typeof sideMap];

      if (DynamicModal) {
        setSideComponent(() => DynamicModal);
      }
    } else {
      if (sideComponentTimeoutRef.current) {
        clearTimeout(sideComponentTimeoutRef.current);
      }

      if (activeSide === 'paymentScreen') {
        setSideComponent(null);
      } else {
        sideComponentTimeoutRef.current = setTimeout(() => {
          setSideComponent(null);
        }, 250);
      }
    }

    return () => {
      if (sideComponentTimeoutRef.current) {
        clearTimeout(sideComponentTimeoutRef.current);
      }
    };
  }, [activeSide]);

  useBodyScrollLock(Boolean(activeSide));

  return (
    <>
      {activeSide && (
        <div
          className="fixed top-0 inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40 "
          onClick={() => {
            if (activeSide === 'paymentScreen') {
              closeCheckout();
              logHubxEvent({
                eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
                event_params: {
                  paywallType,
                },
              });
            }
            dispatch(setTypeForUserAccessLevel(null));
            dispatch(setFreeTypeForUserAccessLevel(null));
            dispatch(hideSide());
          }}
        ></div>
      )}

      <div
        className={clsx(
          'fixed top-0 bottom-0 overflow-hidden right-0 bg-white transition-all  flex justify-center  border-gray-medium2 w-full lg:max-w-[1500px] lg:w-[95%] z-[1000] ',
          activeSide ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="relative w-full h-full overflow-y-auto overscroll-contain z-50">
          <div
            className="!z-[2000] absolute top-8 right-8 md:top-16 md:right-16 cursor-pointer h-8 w-8 flex justify-center items-center rounded-md transition-all"
            onClick={() => {
              if (activeSide === 'paymentScreen') {
                closeCheckout();
                logHubxEvent({
                  eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
                  event_params: {
                    paywallType,
                  },
                });
              }
              dispatch(setTypeForUserAccessLevel(null));
              dispatch(setFreeTypeForUserAccessLevel(null));
              dispatch(hideSide());
            }}
          >
            <FontAwesomeIcon
              icon={['far', 'circle-xmark']}
              className="text-gray-light"
            />
          </div>

          {SideComponent && (
            <div className={clsx('transition-transform duration-250')}>
              <SideLazyErrorBoundary key={activeSide}>
                <SideComponent data={sideData} />
              </SideLazyErrorBoundary>
            </div>
          )}

          {initialSideList.map((side) => {
            const DynamicModal = sideMap[side as keyof typeof sideMap];
            const [isHidden, setIsHidden] = useState(activeSide !== side);
            const timeoutRef = useRef<NodeJS.Timeout>();

            useEffect(() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }

              if (activeSide !== side) {
                timeoutRef.current = setTimeout(() => {
                  setIsHidden(true);
                }, 250);
              } else {
                setIsHidden(false);
              }

              return () => {
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }
              };
            }, [activeSide, side]);

            return (
              <div
                className={clsx('transition-transform duration-250', {
                  'translate-x-0': activeSide !== side,
                  hidden: isHidden,
                })}
                key={side}
              >
                <DynamicModal key={side} data={sideData} />
              </div>
            );
          })}
          <div className="md:hidden">
            <Footer></Footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default Side;
